import { ApolloError } from '@apollo/client';

import { ErrorCode } from '@utils/errorCodes';

type GraphQLErrorLike = {
  message?: string | null;
  extensions?: { code?: string } | null;
};

// Codes that mean "your session or your permissions are the problem". These
// must never be read as not-found: the errorLink's retry-then-logout path
// depends on reaching them, so a `{ code: 'UNAUTHENTICATED', message: '…could
// not be found' }` must stay an auth failure.
const AUTH_ERROR_CODES = new Set<string>([
  ErrorCode.UNAUTHENTICATED,
  ErrorCode.PERMISSION_DENIED,
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_AUTHORIZED',
]);

// Codes that mean "the record does not exist". `RESOURCE_NOT_FOUND` is the
// supported one; the aliases are the shapes `getPublicTeam` has been seen to
// answer a bad uuid with in environments that predate the P01-P03 rollout.
const NOT_FOUND_ERROR_CODES = new Set<string>([
  ErrorCode.RESOURCE_NOT_FOUND,
  'NOT_FOUND',
  'RECORD_NOT_FOUND',
  'TEAM_NOT_FOUND',
]);

// The legacy wording, minus the one subject it must never answer for. A 401
// saying "User could not be found" is a revoked or deleted account, not a
// missing record: reading it as not-found would skip retry-then-logout and
// leave the dead session signed in. The wording fallback exists for the
// *resource* a stale uuid points at, so the principal is excluded by name.
const SESSION_SUBJECT_WORDING =
  /\b(user|account)\b[^.]{0,40}could not be found/;

const hasNotFoundWording = (message?: string | null) => {
  if (!message) return false;

  const text = message.toLowerCase();
  return (
    text.includes('could not be found') && !SESSION_SUBJECT_WORDING.test(text)
  );
};

/**
 * True when a single GraphQL error says the record does not exist.
 *
 * A present code is authoritative: only a code in `NOT_FOUND_ERROR_CODES`
 * counts. Any other code — including one this client has never seen — is left
 * to the errorLink, which treats an unrecognized code on a 401 as a broken
 * session and logs the user out rather than rendering a friendly screen over
 * it. The wording is the fallback for errors that carry no code at all.
 *
 * `allowWordingFallback: false` drops that fallback, so only an explicit
 * not-found code counts. The errorLink passes it for every operation but the
 * team reads that render the unavailable screen: a codeless 401 saying "User
 * could not be found" is a revoked account, and reading it as not-found would
 * skip the retry-then-logout path and leave the dead session signed in.
 *
 * Exported for the errorLink, which walks `graphQLErrors` one at a time on a
 * 200 response.
 */
export const isNotFoundGraphQLError = (
  error?: GraphQLErrorLike | null,
  { allowWordingFallback = true }: { allowWordingFallback?: boolean } = {}
) => {
  if (!error) return false;

  const code = error.extensions?.code;
  if (code) return NOT_FOUND_ERROR_CODES.has(code);

  if (!allowWordingFallback) return false;

  return hasNotFoundWording(error.message);
};

// Codes that describe a different failure entirely. A server fault whose
// message happens to contain the not-found wording must keep being reported
// and must not render the unavailable screen — the wording fallback exists
// for errors this client cannot classify, not for ones it can.
const OTHER_FAILURE_ERROR_CODES = new Set<string>([
  ErrorCode.INTERNAL_ERROR,
  ErrorCode.SERVICE_UNAVAILABLE,
  ErrorCode.RATE_LIMIT_EXCEEDED,
  ErrorCode.VALIDATION_FAILED,
  ErrorCode.CONFLICT,
  ErrorCode.SUBSCRIPTION_REQUIRED,
  ErrorCode.RESULTS_TRUNCATED,
]);

/**
 * The same question for a GraphQL error that arrived on a **200** response.
 *
 * No session verdict is being made here: a 200 never reaches the errorLink's
 * 401 branch, so an unrecognized code is not "log the user out", it is simply
 * a code this client has not catalogued. Being strict about it left the real
 * `getPublicTeam` answer — the not-found wording under a code outside the
 * alias list — unclassified, and the screen sat on its skeletons forever.
 *
 * So the wording counts — but only for a code this client has no meaning for.
 * An explicit auth failure, or a code that already describes a different
 * failure (INTERNAL_ERROR, SERVICE_UNAVAILABLE, …), keeps its own meaning and
 * stays reportable. The strict, code-only rule stays where a session decision
 * actually depends on it: the 401 body.
 */
export const isNotFoundGraphQLErrorOn200 = (
  error?: GraphQLErrorLike | null
) => {
  if (!error) return false;

  const code = error.extensions?.code;
  if (code && NOT_FOUND_ERROR_CODES.has(code)) return true;
  if (
    code &&
    (AUTH_ERROR_CODES.has(code) || OTHER_FAILURE_ERROR_CODES.has(code))
  ) {
    return false;
  }

  return hasNotFoundWording(error.message);
};

/**
 * True when a 401 body carries an error code this client does not recognize
 * as either a not-found or a known permission/session code.
 *
 * Such a response is an account state we cannot reason about, so the errorLink
 * logs out immediately instead of retrying — a retry with the same token can
 * only produce the same unknown verdict, and leaving the session open risks
 * keeping a revoked account signed in.
 *
 * A code this client already catalogues as a different failure
 * (INTERNAL_ERROR, SERVICE_UNAVAILABLE, …) is *not* unrecognized: a 401
 * carrying a transient server fault, or the startup token race the retry
 * guard exists for, must still reach the retry rather than dropping the user
 * on the login screen.
 */
export const unrecognizedAuthErrorCodes = (networkError: unknown): string[] => {
  const errors = (networkError as { result?: { errors?: GraphQLErrorLike[] } })
    ?.result?.errors;
  if (!errors?.length) return [];

  return errors
    .map((error) => error?.extensions?.code)
    .filter(
      (code): code is string =>
        !!code &&
        !NOT_FOUND_ERROR_CODES.has(code) &&
        !AUTH_ERROR_CODES.has(code) &&
        !OTHER_FAILURE_ERROR_CODES.has(code)
    );
};

export const hasUnrecognizedAuthErrorCode = (networkError: unknown): boolean =>
  unrecognizedAuthErrorCodes(networkError).length > 0;

/**
 * True when a network error carries "this record does not exist for you".
 *
 * Apollo puts the response body under `networkError.result` for a non-2xx, so
 * `graphQLErrors` is empty for the HTTP 401 the backend answers a missing team
 * with. The extension code is the supported signal; the wording is the
 * fallback for environments that predate the P01-P03 error-code rollout.
 */
export const isResourceNotFoundError = (
  networkError: unknown,
  { allowWordingFallback = true }: { allowWordingFallback?: boolean } = {}
): boolean => {
  const errors = (networkError as { result?: { errors?: GraphQLErrorLike[] } })
    ?.result?.errors;

  // `every`, not `some`: a body carrying both RESOURCE_NOT_FOUND and
  // UNAUTHENTICATED still contains an authentication failure, and suppressing
  // the retry/logout for it would leave an expired session signed in.
  return (
    !!errors?.length &&
    errors.every((error) =>
      isNotFoundGraphQLError(error, { allowWordingFallback })
    )
  );
};

// The reads that render the unavailable screen. Only these may classify a
// *codeless* 401 body by its wording: the fallback exists for environments
// that predate the P01-P03 error-code rollout, where `getPublicTeam` answers
// a bad uuid with the wording alone. Everywhere else a codeless 401 is a
// session verdict — `{"errors":[{"message":"User could not be found"}]}` is a
// revoked account, and it has to reach retry-then-logout.
export const NOT_FOUND_WORDING_OPERATIONS = new Set<string>([
  'getTeam',
  'getPublicTeam',
  'getTeamDetail',
]);

/**
 * The same verdict for a whole ApolloError, whichever shape it arrived in:
 * a GraphQL error on a 200, or a body-only error on a non-2xx.
 *
 * Structured errors decide on their own. Apollo folds their text into the
 * top-level `message`, so consulting it afterwards would read
 * `{ code: 'UNAUTHENTICATED', message: '…could not be found' }` as not-found —
 * the errorLink treats that as an auth failure and logs out, and this would
 * render the unavailable screen over it. The top-level message is only
 * consulted when the error carries no structured payload at all.
 */
export const isApolloResourceNotFound = (
  error?: ApolloError | null
): boolean => {
  if (!error) return false;

  const bodyErrors = (
    error.networkError as { result?: { errors?: GraphQLErrorLike[] } } | null
  )?.result?.errors;
  const graphQLErrors = error.graphQLErrors ?? [];

  // Each side is judged by the rule for its own transport: strict for the 401
  // body, where the session decision lives, and by wording for a 200, where it
  // does not. `every` on both, and an absent side is vacuously true — so a
  // mixed payload cannot render the unavailable screen over an error the link
  // is logging out for.
  if (graphQLErrors.length || bodyErrors?.length) {
    return (
      graphQLErrors.every(isNotFoundGraphQLErrorOn200) &&
      (bodyErrors ?? []).every((bodyError) => isNotFoundGraphQLError(bodyError))
    );
  }

  // Any network error at all is the link's business, and the link reads the
  // body — never the top-level message. Falling through to the wording for a
  // transport error with no readable body (or with an empty `errors` array)
  // would have the UI render the unavailable screen while the link retries
  // and logs out. The wording fallback is for an ApolloError that carries no
  // structured payload of either kind.
  if (error.networkError) return false;

  return isNotFoundGraphQLError({ message: error.message });
};
